import { Graph } from './graph';

export type TicketWithDependencies = {
  ticketConfigId: number;
  dependsOn: number[];
};

type InternalTicket = {
  position: number;
  ticketConfigId: number;
  dependsOn: number[];
};

export class TicketDependencyGraphBuilder {
  private graph: Graph;
  private sourceNode: number;
  private sinkNode: number;

  private tickets: InternalTicket[] = [];
  private ticketsByTc: Map<number, InternalTicket[]> = new Map();

  constructor(tickets: TicketWithDependencies[]) {
    this.graph = new Graph(tickets.length + 2);
    this.sourceNode = tickets.length;
    this.sinkNode = tickets.length + 1;
    this.preprocessTickets(tickets);
    this.fillGraph();
  }

  private preprocessTickets(tickets: TicketWithDependencies[]) {
    for (let i = 0; i < tickets.length; i++) {
      // Add graph node numbers to all tickets for easier calculations
      const ticket = {
        position: i,
        dependsOn: tickets[i].dependsOn,
        ticketConfigId: tickets[i].ticketConfigId
      };
      this.tickets.push(ticket);

      // arrange tickets by their ticket ids for faster access
      if (this.ticketsByTc.has(ticket.ticketConfigId)) {
        this.ticketsByTc.get(ticket.ticketConfigId).push(ticket);
      } else {
        this.ticketsByTc.set(ticket.ticketConfigId, [ticket]);
      }
    }
  }

  /**
   * Adds connections between:
   * - the source node and all tickets which have dependencies
   * - the tickets with dependencies and their dependencies
   * - the sink node and all tickets upon which other tickets depend
   */
  private fillGraph() {
    for (let i = 0; i < this.tickets.length; i++) {
      const ticket = this.tickets[i];
      // find which tickets this one depends on and add connections
      for (const tcId of ticket.dependsOn) {
        // since this ticket has some tickets on which it depends on,
        // the source node is linked to it
        this.graph.addEdge(this.sourceNode, ticket.position);

        const antecedentTickets = this.ticketsByTc.get(tcId) ?? [];
        for (const at of antecedentTickets) {
          this.graph.addEdge(ticket.position, at.position);

          // since a ticket depends on this one, it is linked to the sink
          // this enables handling transient dependencies
          this.graph.addEdge(at.position, this.sinkNode);
        }
      }
    }
  }

  build() {
    return {
      graph: this.graph,
      sourceNode: this.sourceNode,
      sinkNode: this.sinkNode
    };
  }
}
